/**
 * @title TradeIn Page
 * @description The main tradein page, the consumer can add or select items to tradein here
 * @image https://storageaccountourimbea2.blob.core.windows.net/tradein2guide/mainpage.png
 */
import * as React from "react";
import {FC, useEffect, useState} from "react";
import {PrivateRenderer} from "../PrivateRenderer";
import {useRecoilState, useRecoilValue} from "recoil";
import {AccessContextData} from "../../../providers/access/AppContextProvider";
import {GlobalLayout, StyledCheckbox, StyledDiv, StyledTypography} from "../../../providers/layout/LayoutProvider";
import {makeStyles} from "@mui/styles";
import {
   ConsumerPotentialFragment,
   EnumTradeInPayoutType,
   PotentialSourceTypeEnum,
   ReturnsPageConsumerDataDocument,
   useReturnsPageConsumerDataQuery,
   useReturnsPagePotentialDeleteMutation,
   useReturnsPageWholeSaleUploadCreateMutation,
   WholeSaleCategory
} from "../../../Queries";
import {isPreview} from "../PreviewContext";
import {Box, IconButton, Paper} from "@mui/material";
import {CloseIcon} from "../../../svg/SvgIcons";
import {Dialogs} from "../../../Dialog";
import {AddItemDialog, SpecificProduct} from "./dialog/AddItemDialog";
import {AppDataAtom} from "../../../providers/access/ApplicationDataProvider";
import {ReturnsCartAtom} from "./ReturnsCart";
import {ImageView} from "../../../components/ImageView";
import clsx from "clsx";
import {LocalizationsAtom} from "../../../providers/localization/LocalizationProvider";
import {Currency} from "../../../components/Currency";
import {Link} from "react-router-dom";
import {HistoryPagePath} from "../history/HistoryPage";
import {AddButton} from "./AddButton";
import {Analytics, AnalyticsEvent} from "../../../analytics/Analytics";
import {Progress} from "../../../components/Progress";
import {ComputerIcon, StorefrontIcon} from "../../../components/Icons";
import {Unstable_Popup as BasePopup} from '@mui/base/Unstable_Popup';
import {DateFormat} from "../../../utility/DateFormat";
import {AddAnotherDialog} from "./dialog/AddAnotherDialog";
import {PubSub} from "../../../utility/PubSub";

type ItemType = PotentialSourceTypeEnum

export const ReturnsPagePath = () => {
   return "/main";
}

function getItemType(potential: ConsumerPotentialFragment): ItemType {
   if (potential?.sourceType) {
      return potential.sourceType;
   }
   if (potential?.wholeSale) {
      return PotentialSourceTypeEnum.Wholesale;
   } else if (potential?.consumer?.accountType === 'RECIEPT') {
      return PotentialSourceTypeEnum.Reciept;
   } else {
      return PotentialSourceTypeEnum.Online;
   }
}

export const ReturnsPage: FC<any> = () => {
   type PotentialData = {
      wsItems: ConsumerPotentialFragment[]
      b2cItems: ConsumerPotentialFragment[]
      disabledItems: ConsumerPotentialFragment[]
      noItems: boolean
   }
   const [showAddAnother, setShowAddAnother] = useState<boolean>(false);
   const localizations = useRecoilValue(LocalizationsAtom);
   const [cart, setCart] = useRecoilState(ReturnsCartAtom)
   const [potentialData, setPotentialData] = useState<PotentialData | undefined>(undefined)
   const appContext = useRecoilValue(AccessContextData);
   const [adding, setAdding] = useState<boolean>(false)
   const appData = useRecoilValue(AppDataAtom);
   const classes = useStyles();
   const {data, refetch} = useReturnsPageConsumerDataQuery({
      variables: {},
      fetchPolicy: 'network-only'
   })
   const [createItem] = useReturnsPageWholeSaleUploadCreateMutation()
   const [deleteItem] = useReturnsPagePotentialDeleteMutation()

   useEffect(() => {
      if (!appContext?.allowedCountry) {
         Dialogs.info({
            title: localizations.countryWarningTitle,
            subtitle: localizations.countryWarningDescription
         });

         Analytics.event(AnalyticsEvent.trackServiceDenied, {
            allowedCountry: "false"
         });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [appContext?.allowedCountry]);

   useEffect(() => {
      if (appContext.requestPotentialsSupported && appContext?.requestPotentialTokens?.length === 0) {
         console.log('Refetching potentials')
         refetch()
      }
   }, [appContext?.requestPotentialTokens, appContext.requestPotentialsSupported, refetch]);

   useEffect(() => {
      if (cart.version === 0 && data?.ConsumerPotentials?.length) {
         const wsItems = data.ConsumerPotentials?.filter(pot => {
            return !!pot?.wholeSale
         }) || []
         setCart({
            potentials: wsItems as ConsumerPotentialFragment[],
            version: cart.version + 1,
            payoutType: EnumTradeInPayoutType.Voucher,
            comments: cart.comments || []
         });
      }
   }, [cart, data, setCart])

   useEffect(() => {
      const disabledItems: ConsumerPotentialFragment[] = [];
      const b2cItems: ConsumerPotentialFragment[] = [];
      const wsItems: ConsumerPotentialFragment[] = [];
      if (data) {
         for (const potential of data.ConsumerPotentials || []) {
            if (potential?.wholeSale) {
               wsItems.push(potential);
            } else if (potential) {
               if (!potential.offer?.validForTradeIn) {
                  disabledItems.push(potential);
               } else {
                  b2cItems.push(potential);
               }
            }
         }
         let noItems = (b2cItems.length + wsItems.length + disabledItems.length) === 0;
         setPotentialData({
            wsItems,
            b2cItems,
            disabledItems,
            noItems
         })
      }
   }, [data])

   useEffect(() => {
      if (!appContext.allowedCountry) {
         return
      }
      if (cart.potentials.length === 0 && appContext?.headless && appContext.wholeSaleActive) {
         setAdding(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   if (!data || !potentialData) {
      return <Progress/>
   }

   const onAddItem = (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      setAdding(true);
      setShowAddAnother(false);
   }

   const onAddHide = (cancel: boolean) => {
      setAdding(false);
      if(!cancel) {
         // When dialog closes, show the "add another" option
         setShowAddAnother(true);
      }
   };

   const onCreate = async (category: WholeSaleCategory, imageId?: string, specificProduct?: SpecificProduct) => {
      const newItem = await createItem({
         variables: {
            consumerId: appContext.consumerId!,
            categoryId: category?._id,
            imageId: imageId,
            ruleId: specificProduct?.product?.ruleId,
            brandProductVariantId: specificProduct?.product?.id
         },
         refetchQueries: [{
            query: ReturnsPageConsumerDataDocument,
            variables: {
               preview: isPreview()
            }
         }],
         awaitRefetchQueries: true
      });
      setAdding(false);
      setShowAddAnother(true); // Show the add another option after adding an item

      setCart({
         potentials: [...cart.potentials, newItem.data?.PotentialCreate as ConsumerPotentialFragment],
         version: cart.version + 1,
         payoutType: EnumTradeInPayoutType.Voucher,
         comments: cart.comments || []
      });

      Analytics.event(AnalyticsEvent.trackItemAdd, {
         potentialId: newItem.data?.PotentialCreate?._id,
         wholeSaleCategory: category?.name,
         wholeSaleValue: category?.value ? String(category?.value) : undefined,
         wholeSaleProduct: specificProduct?.product?.styleId
      });
   };

   return <PrivateRenderer step={"returns"}>
      {adding &&
         <AddItemDialog onHide={onAddHide} onCreate={onCreate}
                        enableProductSearch={appData.productSearchEnabled || false}/>}
      {showAddAnother &&
         <AddAnotherDialog
            open={showAddAnother}
            onHide={(noThanks) => {
               setShowAddAnother(false)
               if(noThanks) {
                  PubSub.emit('navigation-forward');
               }
            }}
            onAddAnother={onAddItem}
         />
      }
      <StyledDiv type={"main"} className={classes.page}>
         <div className={classes.pageInner}>
            {((appData.productSearchEnabled || appData.wholeSaleActive) && potentialData.b2cItems.length === 0 && potentialData.wsItems.length === 0) &&
               <div className={classes.pageTitle}>
                  <StyledTypography variant={"body1"}
                                    className={"c2s-returns-description-none"}
                                    dangerouslySetInnerHTML={{__html: localizations.returnsDescriptionNone}}
                  />
               </div>}

            {(!appData.productSearchEnabled && !appData.wholeSaleActive && potentialData.b2cItems.length === 0) && <div className={classes.pageTitle}>
               <StyledTypography variant={"h2"}>{localizations.returnsNothingTitle}</StyledTypography>
               <StyledTypography variant={"body1"}>{localizations.returnsNothingDesc}</StyledTypography>
            </div>}

            {(!adding && (appData.wholeSaleActive || appData.productSearchEnabled)) &&
               <Box>
                  <AddButton isEmpty={potentialData.noItems || false} onClick={onAddItem}>
                     {potentialData.noItems ? localizations.returnsAddItem : (localizations.returnsAddItemMore || localizations.returnsAddItem)}
                  </AddButton>
               </Box>
            }

            {potentialData.b2cItems.length > 0 &&
               <div className={classes.itemsFrame}>
                  <StyledTypography type={"main"} variant={"h2"}>
                     {localizations.formatString(localizations.returnsTitleDirect, appContext.brandName!)}
                  </StyledTypography>
                  {localizations.returnsTitleSubText && <StyledTypography type={"main"} variant={"body1"}>
                     {localizations.formatString(localizations.returnsTitleSubText, appContext.brandName!)}
                  </StyledTypography>}
                  <Box className={classes.b2cItemsContainer}>
                     {potentialData.b2cItems.map(potential => {
                        const onClick = () => {
                           if (cart.potentials.find(pot => pot._id === potential?._id)) {
                              setCart({
                                 potentials: cart.potentials.filter(pot => pot._id !== potential?._id),
                                 version: cart.version + 1,
                                 payoutType: EnumTradeInPayoutType.Voucher,
                                 comments: cart.comments || []
                              })
                           } else {
                              setCart({
                                 potentials: [...cart.potentials, potential as ConsumerPotentialFragment],
                                 version: cart.version + 1,
                                 payoutType: EnumTradeInPayoutType.Voucher,
                                 comments: cart.comments || []
                              })
                           }
                        }
                        let itemType = getItemType(potential);

                        let checked = !!cart.potentials.find(pot => pot?._id === potential?._id)
                        return <ReturnItem key={'return_' + potential?._id} potential={potential!} checked={checked}
                                           onClick={onClick} itemType={itemType}/>
                     })}
                  </Box>
               </div>
            }

            {potentialData.wsItems.length > 0 &&
               <div className={classes.itemsFrame}>
                  <StyledTypography type={"main"} variant={"h2"}>
                     {localizations.returnsTitleRetail}
                  </StyledTypography>
                  <Box className={classes.itemsContainer}>
                     {potentialData.wsItems.map(potential => {
                        const onClick = () => {
                           if (cart.potentials.find(pot => pot._id === potential?._id)) {
                              setCart({
                                 potentials: cart.potentials.filter(pot => pot._id !== potential?._id),
                                 version: cart.version + 1,
                                 payoutType: EnumTradeInPayoutType.Voucher,
                                 comments: cart.comments || []
                              })
                           } else {
                              setCart({
                                 potentials: [...cart.potentials, potential as ConsumerPotentialFragment],
                                 version: cart.version + 1,
                                 payoutType: EnumTradeInPayoutType.Voucher,
                                 comments: cart.comments || []
                              })
                           }
                        }
                        const onDelete = async () => {
                           Dialogs.confirm({
                              title: localizations.returnsConfirmDelete,
                              subtitle: localizations.returnsConfirmDeleteDescription,
                              yesText: localizations.returnsConfirmYes,
                              noText: localizations.returnsConfirmNo,
                           }, async () => {
                              const deletion = await deleteItem({
                                 variables: {
                                    potentialId: potential!._id
                                 },
                                 refetchQueries: [{
                                    query: ReturnsPageConsumerDataDocument,
                                    variables: {
                                       preview: isPreview()
                                    }
                                 }]
                              });
                              if (!deletion.errors?.length) {
                                 setCart({
                                    potentials: cart.potentials.filter(pot => pot._id !== potential?._id),
                                    version: cart.version + 1,
                                    payoutType: EnumTradeInPayoutType.Voucher,
                                    comments: cart.comments || []
                                 })
                              }
                           });
                        };
                        let checked = !!cart.potentials.find(pot => pot?._id === potential?._id)
                        return <ReturnItem key={'return_' + potential?._id} potential={potential!} checked={checked}
                                           onClick={onClick} onDelete={onDelete}
                                           itemType={getItemType(potential)}/>
                     })}
                  </Box>
               </div>
            }

            {potentialData.disabledItems.length > 0 &&
               <div className={classes.itemsFrame} style={{marginTop: '60px'}}>
                  <StyledTypography type={"main"} variant={"h2"}>
                     {localizations.returnsTitleDisallowed}
                  </StyledTypography>
                  <Box className={classes.disallowedItemsContainer}>
                     {potentialData.disabledItems.map(potential => {
                        return <ReturnItem key={'disallowed_' + potential?._id}
                                           potential={potential!}
                                           itemType={getItemType(potential)}
                                           disallowed={true}
                                           checked={false}
                        />
                     })}
                  </Box>
               </div>
            }
         </div>
         {((data?.ConsumerTradeIns?.length || 0) > 0) &&
            <Box sx={{marginTop: '50px'}}>
               <Link to={HistoryPagePath()}> <StyledTypography type={"main"} variant={"body1"}>
                  {localizations.historicPageLink}
               </StyledTypography> </Link>
            </Box>}
      </StyledDiv>
   </PrivateRenderer>
}

const useStyles = makeStyles({
   page: {
      display: 'flex',
      width: '100%',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'start'
   },
   pageInner: {
      display: 'flex',
      width: '90%',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'start'
   },
   pageTitle: {
      width: '440px',
      textAlign: 'center',
      marginBottom: '50px',
      marginTop: '20px'
   },
   '@media only screen and (max-width: 800px)': {
      pageTitle: {
         width: '90%',
         maxWidth: '440px'
      },
   },
   itemsFrame: {
      textAlign: 'center',
      marginTop: '20px'
   },
   b2cItemsContainer: {
      display: 'flex',
      gap: '30px',
      marginTop: '25px',
      flexWrap: 'wrap',
      flexDirection: 'row',
      justifyContent: 'center'
   },
   disallowedItemsContainer: {
      display: 'flex',
      gap: '30px',
      marginTop: '25px',
      flexWrap: 'wrap',
      flexDirection: 'row',
      justifyContent: 'center'
   },
   itemsContainer: {
      display: 'flex',
      gap: '30px',
      marginTop: '10px',
      flexWrap: 'wrap',
      flexDirection: 'row',
      justifyContent: 'center'
   },
   addItem: {
      backgroundColor: 'inherit'
   }
});


type ReturnItemProps = {
   potential: ConsumerPotentialFragment
   checked: boolean
   onClick?: () => void
   onDelete?: () => void
   disallowed?: boolean
   itemType: ItemType
}

const ReturnItem: FC<ReturnItemProps> = ({potential, checked, onClick, onDelete, itemType, disallowed}) => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const classes = useReturnItemStyles();
   const globalLayout = useRecoilValue(GlobalLayout);
   const appData = useRecoilValue(AppDataAtom);

   const image = potential?.imageUrls?.[0] || appData.defaultImage

   let scale = 1;
   if (globalLayout.widgets?.returnItem?.scale) {
      scale = 0.55 + (globalLayout.widgets?.returnItem?.scale / 16);
   }
   let size = 280 * scale;
   if (disallowed) {
      size = 0.5 * size;
   }

   let manuallyCreated = itemType === PotentialSourceTypeEnum.Wholesale || itemType === PotentialSourceTypeEnum.Search;
   return <div className={classes.frame} style={{maxWidth: (size + 50) + 'px', opacity: disallowed ? 0.5 : 1}}>
      <div className={classes.imageContainer}
           style={{zoom: scale, border: globalLayout.widgets?.returnItem?.border || undefined}}>
         <div className={classes.deleteImage}>
            {manuallyCreated &&
               <IconButton onClick={onDelete}>
                  <CloseIcon fontSize={"large"} iconcolor={globalLayout.widgets?.actionItemBorderColor || '#FF5376'}/>
               </IconButton>
            }
            {itemType === PotentialSourceTypeEnum.Reciept &&
               <PopperInfo potential={potential}><StorefrontIcon size={"24px"}/></PopperInfo>
            }
            {itemType === PotentialSourceTypeEnum.Online &&
               <PopperInfo potential={potential}><ComputerIcon size={"24px"}/></PopperInfo>
            }
         </div>
         <ImageView size={size} src={image} title={potential?.name || undefined}/>
      </div>
      <div className={classes.titleContainer}>
         <StyledTypography
            type={"main"}
            variant={"subtitle1"}
            className={classes.title}
            title={(potential?.name || 'Unnamed') + (potential?.size ? ' / ' + potential?.size : '')}
            style={{
               color: globalLayout.widgets?.returnItem?.textColor || undefined,
               fontWeight: globalLayout.widgets?.returnItem?.fontWeight || undefined,
               fontSize: globalLayout.widgets?.returnItem?.fontSize || undefined,
               fontFamily: globalLayout.widgets?.returnItem?.fontFamily || undefined,
            }}
         >
            {potential?.name || '?'}
            {(potential?.size && potential?.size !== 'N/A') && <> / {potential?.size}</>}
         </StyledTypography>
      </div>
      {!disallowed && <div>
         <StyledTypography type={"main"} variant={"subtitle1"} style={{
            color: globalLayout.widgets?.returnItem?.textColor || undefined,
            fontWeight: globalLayout.widgets?.returnItem?.fontWeight || 'bold',
            fontSize: globalLayout.widgets?.returnItem?.fontSize || undefined,
            fontFamily: globalLayout.widgets?.returnItem?.fontFamily || undefined,
         }}>
            {localizations.returnsValue} <Currency amount={potential?.offer?.value} showMainCurrency={true}/>
         </StyledTypography>
      </div>}
      {(!manuallyCreated && !disallowed) && <div className={classes.checkboxContainer} onClick={onClick}>
         <StyledCheckbox checked={checked} color={"secondary"} className={clsx(classes.checkbox, "c2s-item-checkbox")}
                         style={{
                            color: globalLayout.widgets?.returnItem?.textColor || undefined,
                            fontWeight: globalLayout.widgets?.returnItem?.fontWeight || undefined,
                            fontSize: globalLayout.widgets?.returnItem?.fontSize || '16px !important',
                            fontFamily: globalLayout.widgets?.returnItem?.fontFamily || undefined,
                         }}
         />
         <StyledTypography type={"main"} variant={"subtitle1"} className={classes.checkboxText}>
            {localizations.returnsConfirmTrade}
         </StyledTypography>
      </div>}
   </div>
}


const useReturnItemStyles = makeStyles({
   frame: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: "center",
      position: 'relative',
      flexGrow: '1',
      flexBasis: 'max-content',
   },
   image: {
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      backgroundPosition: "center",
      cursor: 'pointer',
      width: '84px',
      height: '90px'
   },
   deleteImage: {
      position: 'absolute',
      right: '1px',
      top: '1px',
      cursor: 'pointer'
   },
   imageContainer: {
      border: '3px solid #E0E0E0',
      padding: '17px',
      marginBottom: '10px',
      position: 'relative'
   },
   checkbox: {
      marginLeft: '-10px',
      "& svg": {
         fontSize: '130%',
         color: 'black'
      },
      "& input": {
         margin: 0
      }
   },
   checkboxContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: "center",
      alignItems: 'center',
      cursor: 'pointer'
   },
   checkboxText: {
      fontWeight: 'bold'
   },
   titleContainer: {
      display: 'grid'
   },
   title: {
      textOverflow: 'ellipsis',
      width: '100%',
      overflow: 'hidden',
      whiteSpace: 'nowrap'
   }
});


type PopperInfoProps = {
   potential: ConsumerPotentialFragment
   children: React.ReactNode | React.ReactNode[] | null;
   autoHideDelay?: number; // Optional prop to customize hide delay
}

const PopperInfo = ({
                       potential,
                       children,
                       autoHideDelay = 5000 // Default to 5 seconds
                    }: PopperInfoProps) => {
   const [anchor, setAnchor] = React.useState<null | HTMLButtonElement>(null);
   const localizations = useRecoilValue(LocalizationsAtom);

   useEffect(() => {
      let timeoutId: NodeJS.Timeout;

      if (anchor) {
         timeoutId = setTimeout(() => {
            setAnchor(null);
         }, autoHideDelay);
      }

      // Cleanup function to clear timeout if popper is manually closed
      return () => {
         if (timeoutId) {
            clearTimeout(timeoutId);
         }
      };
   }, [anchor, autoHideDelay]);

   const handleCLick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchor(anchor ? null : event.currentTarget);
   }
   const itemType = getItemType(potential);

   const open = Boolean(anchor);
   const id = open ? 'simple-popup' : undefined;

   return <Box>
      <IconButton size={"small"} onClick={handleCLick}>
         {children}
      </IconButton>
      <BasePopup id={id} open={open} anchor={anchor}>
         <Paper sx={{padding: '20px'}}>
            <StyledTypography type={"main"} variant={"subtitle1"}>
               {itemType === PotentialSourceTypeEnum.Online ? localizations.itemInfoOnlineTitle : undefined}
               {itemType === PotentialSourceTypeEnum.Reciept ? localizations.itemInfoStoreTitle : undefined}
            </StyledTypography>
            <StyledTypography type={"main"}
                              variant={"subtitle1"}>{potential.externDate ? DateFormat.toPresent(new Date(potential.externDate)) : undefined}</StyledTypography>
         </Paper>
      </BasePopup>
   </Box>
}